import { Component, OnInit, AfterViewInit, ViewChild, HostListener, ChangeDetectorRef, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { FormControl, FormGroup, Validators, FormArray, ReactiveFormsModule, FormsModule } from "@angular/forms";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import { IAppStore } from "../../iappstore";
import { Store } from "@ngrx/store";
import { BusinessFunctionService } from "../../services/businessfunction.service";
import { IBusinessFunction } from "../../interfaces/ibusinessfunction";
import { IKeyContact } from "../../interfaces/ikeycontact";
import { AppSettings } from "../../appsettings";
import { BusinessFunctionFeatureDto, ContentIsUpToDateFeatureDto, CountryComparisonFeatureDto, AboutUsFeatureDto, AuditHistoryFeatureDto, PdfDownloadFeatureDto, TimelineFeatureDto, PublishedDateFeatureDto, EmailNotificationFeatureDto } from "../../models/features";
import { IFeature } from "../../interfaces/ifeature";
import { Features } from "../../enums/features";
import { Roles } from "../../enums/roles";
import { SetSelectedBusinessFunctionAction, AddBusinessFunctionAction } from "../../actions/applicationuser.action";
import { ICanComponentDeactivate } from "../../interfaces/icancomponentdeactivate";
import { Observable } from "rxjs";
import { take } from "rxjs/operators";
import { EmailDigestFeatureDto } from "../../models/features/emaildigestfeaturedto";
import { CkEditorEnhancedDirective } from "../../directives/ck-editor-enhanced.directive";
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ConfirmationModalComponent } from "../confirmationmodal/confirmationmodal.component";
import { KeyContactsComponent } from "../keycontacts/keycontacts/keycontacts.component";

/**
 * Business Function component
 * Admin and Super Admin roles can update the business function specific attributes here.
 */
@Component({
    selector: "businessfunction",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, CKEditorModule, CkEditorEnhancedDirective, ConfirmationModalComponent, KeyContactsComponent],
    templateUrl: "./businessfunction.component.html",
    styleUrls: ["./businessfunction.component.css"],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class BusinessFunctionComponent implements OnInit, ICanComponentDeactivate, AfterViewInit {
    public Editor = ClassicEditor;
    businessFunctionForm: FormGroup = new FormGroup({
        // Initialize with basic controls to prevent template errors
        id: new FormControl(null),
        defaultRoute: new FormControl(''),
        displayName: new FormControl(''),
        authenticationRequired: new FormControl(false),
        disclaimers: new FormControl(''),
        deloitteClientAccess: new FormControl(false),
        legalInformation: new FormControl(''),
        pdfLegalInformation: new FormControl(''),
        about: new FormControl(''),
        headerText: new FormControl(''),
        logo: new FormControl(''),
        overviewText: new FormControl(''),
        email: new FormControl(''),
        features: new FormArray([])
    });
    businessFunction: IBusinessFunction;
    portalHeaderMaxLength: number = 120;
    portalOverviewMaxLength: number = 1000;
    pdfLegalMaxLength: number = 1600;
    isSuperAdmin: boolean = false;
    isNewBusinessFunction: boolean = false;
    pathLocked: boolean = true;
    config: any = {};
    features: IFeature[];
    keyContacts: IKeyContact[];
    roles: any = Roles;
    originalData: string;
    encodedImage: any;
    imageErrorMsg: any;
    defaultLogoChecked: any;
    bespokeLogoChecked: any;
    selectedLink: string;
    imageExtension: string;
    imageExtInLowerCase: string;
    logoImage: any;
    removeImageButtonVisibility: boolean;

    @ViewChild("confirmModal") confirmModal;
    @ViewChild('file') imageFile;
    constructor(private toastr: ToastrService,
        private businessFunctionService: BusinessFunctionService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private readonly store: Store<IAppStore>) {

        /*
         * CKEditor Configuration Notes:
         * Using ClassicEditor build with available features only.
         * 
         * Available formatting: Bold, Italic, Headings, Lists, Links, Tables, Blockquotes
         * Indent/Outdent: Work only within lists (bulleted/numbered) - this is normal behavior
         * Missing features (not available in ClassicEditor build):
         * - Underline, Strikethrough
         * - Font size/family, Font colors
         * - Subscript/Superscript
         * - Remove format
         * - Block-level indentation (paragraph indent)
         * 
         * Workarounds implemented:
         * - Enhanced CSS styling for better visual presentation
         * - Improved table, list, and blockquote styling
         * - Manual height control (300px minimum with resizing)
         * - List indentation works when cursor is inside a list item
         * 
         * How to use indent buttons:
         * 1. Create a bulleted or numbered list
         * 2. Place cursor inside a list item
         * 3. Indent/outdent buttons will become active
         * 
         * To get missing features, consider:
         * 1. CKEditor 5 Online Builder (https://ckeditor.com/ckeditor-5/online-builder/)
         * 2. Different editor library (TinyMCE, Quill, etc.)
         * 3. HTML keyboard shortcuts guidance for users
         */

        // Final working configuration for ClassicEditor
        this.config = {
            toolbar: [
                'heading',
                '|',
                'bold',
                'italic',
                '|',
                'bulletedList',
                'numberedList',
                '|',
                'outdent',
                'indent',
                '|',
                'link',
                'blockQuote',
                '|',
                'undo',
                'redo'
            ],
            heading: {
                options: [
                    { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                    { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                    { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                    { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                    { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' }
                ]
            },
            link: {
                // Enable "Open in new tab" option for links
                decorators: {
                    openInNewTab: {
                        mode: 'manual',
                        label: 'Open in a new tab',
                        attributes: {
                            target: '_blank',
                            rel: 'noopener noreferrer'
                        }
                    }
                }
            },
            // Add height configuration for scrollable editor
            height: 400,
            // GPL license key for open-source CKEditor 5
            licenseKey: 'GPL',
            balloonToolbar: [],
            ui: {
                poweredBy: {
                    forceVisible: false,
                    label: null,
                    side: 'right'
                }
            }
        };
    }

    /**
     * On initailse of an business function component.
     * Here it initailse the form contents and load the data to display.
     */
    ngOnInit(): void {
        this.features = this.activatedRoute.snapshot.data["features"] as IFeature[];
        this.store.select(e => e.user)
            .subscribe(r => {
                // Set SuperAdmin role first
                this.isSuperAdmin = r.roles && r.roles.find(role => role === "SuperAdmin") ? true : false;

                this.store.select(e => e.user.selectedBusinessFunction)
                    .subscribe(sbf => {
                        if (sbf && sbf.id) {
                            this.businessFunction = sbf;
                            // Initialize form after we have both role and business function data
                            this.initialiseForm();
                        }
                    });

                if (!this.businessFunction) {
                    this.isNewBusinessFunction = true;
                    this.businessFunction = {
                        lastUpdatedDate: new Date,
                        id: 0,
                        displayName: "",
                        defaultRoute: "",
                        headerText: "",
                        overviewText: "",
                        email: "",
                        disclaimers: "",
                        legalInformation: "",
                        pdfLegalInformation: "",
                        authenticationRequired: true,
                        deloitteClientAccess: false,
                        about: "",
                        features: new Array<BusinessFunctionFeatureDto>(),
                        keyContacts: new Array<IKeyContact>(),
                        logo: ""
                    };
                    this.pathLocked = false;
                    // Initialize form for new business function
                    this.initialiseForm();
                }
            });
        this.businessFunctionService.getImage(this.businessFunction.id).subscribe(image => {
            this.logoImage = image;
            if (this.logoImage == undefined || this.logoImage == null || this.logoImage == "") {
                this.selectedLink = "Default";
                this.encodedImage = this.logoImage;
                this.defaultLogoChecked = 'checked';
                this.bespokeLogoChecked = '';
            }
            else {
                this.selectedLink = "Bespoke";
                this.encodedImage = this.logoImage;
                this.bespokeLogoChecked = 'checked';
                this.defaultLogoChecked = '';
                this.removeImageButtonVisibility = true;
            }
            this.cdr.detectChanges();
        });
    }

    initialiseForm(): void {
        if (this.isSuperAdmin) {
            if (this.businessFunction != null) {
                // Create a mutable copy to avoid read-only property issues
                const defaultRoute = this.businessFunction.defaultRoute ? this.businessFunction.defaultRoute.replace("/", "") : "";
                this.logoImage = this.businessFunction.logo;
                this.businessFunctionForm = new FormGroup({
                    id: new FormControl(this.businessFunction.id),
                    defaultRoute: new FormControl(defaultRoute, [
                        Validators.required, Validators.pattern(RegExp(/^[0-9a-z]+$/m))
                    ]),
                    displayName: new FormControl(this.businessFunction.displayName, [
                        Validators.required
                    ]),
                    authenticationRequired: new FormControl(this.businessFunction.authenticationRequired),
                    disclaimers: new FormControl(this.businessFunction.disclaimers, [
                        Validators.required
                    ]),
                    deloitteClientAccess: new FormControl(this.businessFunction.deloitteClientAccess),
                    legalInformation: new FormControl(this.businessFunction.legalInformation, [
                        Validators.required
                    ]),
                    pdfLegalInformation: new FormControl(this.businessFunction.pdfLegalInformation, [
                        Validators.maxLength(this.pdfLegalMaxLength)
                    ]),
                    about: new FormControl(this.businessFunction.about),
                    headerText: new FormControl(this.businessFunction.headerText, [
                        Validators.required,
                        Validators.maxLength(this.portalHeaderMaxLength)
                    ]),
                    logo: new FormControl(this.logoImage),
                    overviewText: new FormControl(this.businessFunction.overviewText, [Validators.maxLength(this.portalOverviewMaxLength)]),
                    email: new FormControl(this.businessFunction.email, [
                        Validators.required,
                        Validators.pattern(AppSettings.emailRegExp.source)]),
                    features: new FormArray([])
                });

                const control: FormArray = <FormArray>this.businessFunctionForm.controls["features"];
                // add features
                this.features.forEach(feature => {
                    switch (feature.id) {
                        default:
                            const enabled: BusinessFunctionFeatureDto = this.businessFunction.features.find(e => e.featureId === feature.id);
                            control.push(new FormGroup({
                                featureId: new FormControl(feature.id),
                                name: new FormControl(feature.name),
                                enabled: new FormControl(enabled ? enabled.enabled : false)
                            }));
                            break;
                    }
                });
            }
        } else {
            if (this.businessFunction != null) {
                this.businessFunctionForm = new FormGroup({
                    id: new FormControl(this.businessFunction.id),
                    headerText: new FormControl(this.businessFunction.headerText, [
                        Validators.required,
                        Validators.maxLength(this.portalHeaderMaxLength)
                    ]),
                    overviewText: new FormControl(this.businessFunction.overviewText, [Validators.maxLength(this.portalOverviewMaxLength)]),
                    email: new FormControl(this.businessFunction.email, [
                        Validators.required,
                        Validators.pattern(AppSettings.emailRegExp.source)
                    ]),
                    about: new FormControl(this.businessFunction.about, [Validators.required])
                });
            }
        }
    }

    /**
     * Save the business function.
     */
    updateBusinessFunction(businessFunction: IBusinessFunction, valid: boolean): void {
        if (this.encodedImage == "" && this.selectedLink != "Default") {
            this.toastr.error("Please upload an image before continuing or choose default");
        }
        else {
            if (valid) {
                // Get current user data synchronously to avoid subscription loops
                let currentUser: any;
                this.store.select(e => e.user).pipe(take(1)).subscribe(usr => currentUser = usr);

                if (currentUser && currentUser.selectedBusinessFunction && currentUser.selectedBusinessFunction.id) {
                    // Get the features array from the form
                    const featuresFormArray = this.businessFunctionForm.get('features') as FormArray;
                    const formFeatures = featuresFormArray ? featuresFormArray.value : [];

                    // Create a proper business function object preserving all original properties
                    const finalBusinessFunction: IBusinessFunction = {
                        // Preserve all original properties from the existing business function
                        lastUpdatedDate: this.businessFunction.lastUpdatedDate,
                        id: businessFunction.id || this.businessFunction.id,
                        displayName: this.isSuperAdmin ? (businessFunction.displayName || this.businessFunction.displayName) : this.businessFunction.displayName,
                        defaultRoute: this.isSuperAdmin ?
                            (businessFunction.defaultRoute && businessFunction.defaultRoute.startsWith("/") ? businessFunction.defaultRoute : "/" + businessFunction.defaultRoute) :
                            this.businessFunction.defaultRoute,
                        headerText: businessFunction.headerText || this.businessFunction.headerText,
                        overviewText: businessFunction.overviewText,
                        email: businessFunction.email || this.businessFunction.email,
                        disclaimers: this.isSuperAdmin ? businessFunction.disclaimers : this.businessFunction.disclaimers,
                        legalInformation: this.isSuperAdmin ? businessFunction.legalInformation : this.businessFunction.legalInformation,
                        pdfLegalInformation: this.isSuperAdmin ? businessFunction.pdfLegalInformation : this.businessFunction.pdfLegalInformation,
                        authenticationRequired: this.isSuperAdmin ? (businessFunction.authenticationRequired !== undefined ? businessFunction.authenticationRequired : this.businessFunction.authenticationRequired) : this.businessFunction.authenticationRequired,
                        deloitteClientAccess: this.isSuperAdmin ? (businessFunction.deloitteClientAccess !== undefined ? businessFunction.deloitteClientAccess : this.businessFunction.deloitteClientAccess) : this.businessFunction.deloitteClientAccess,
                        about: businessFunction.about,
                        logo: this.encodedImage,
                        features: this.isSuperAdmin ? this.mapFeaturesToDtos(formFeatures) : this.businessFunction.features,
                        keyContacts: currentUser.selectedBusinessFunction.keyContacts
                    };

                    this.businessFunctionService.update(finalBusinessFunction).subscribe(() => {
                        setTimeout(() => this.toastr.success("Business function saved"));
                        // Update the local business function with the saved data
                        this.businessFunction = finalBusinessFunction;
                        this.store.dispatch(new SetSelectedBusinessFunctionAction(finalBusinessFunction));
                        this.originalData = JSON.stringify(this.businessFunctionForm.value);
                        this.businessFunctionService.getImage(this.businessFunction.id).subscribe(image => {
                            this.logoImage = image;
                        });
                    });
                }
            } else {
                setTimeout(() => this.toastr.error("Please correct errors before continuing"));
            }
        }
    }
    createBusinessFunction(businessFunction: IBusinessFunction, valid: boolean): void {
        if (valid) {
            businessFunction.defaultRoute = businessFunction.defaultRoute.startsWith("/") ? businessFunction.defaultRoute : "/" + businessFunction.defaultRoute;

            // Get the features array from the form
            const featuresFormArray = this.businessFunctionForm.get('features') as FormArray;
            const formFeatures = featuresFormArray ? featuresFormArray.value : [];
            businessFunction.features = this.mapFeaturesToDtos(formFeatures);

            this.businessFunctionService.create(businessFunction).subscribe(r => {
                this.businessFunctionForm.controls["id"].setValue(r);
                businessFunction.id = r;
                setTimeout(() => this.toastr.success("Business function created"));
                this.isNewBusinessFunction = false;
                this.businessFunction = businessFunction;
                this.store.dispatch(new AddBusinessFunctionAction(businessFunction));
                this.store.dispatch(new SetSelectedBusinessFunctionAction(businessFunction));
                this.originalData = JSON.stringify(this.businessFunctionForm.value);
            });
        } else {
            setTimeout(() => this.toastr.error("Please correct errors before continuing"));
        }
    }

    /**
     * Map the business function features back to the C# dtos
     * @param features the forms dto's to map to the business function dtos
     */
    mapFeaturesToDtos(features: any): BusinessFunctionFeatureDto[] {
        const feat: BusinessFunctionFeatureDto[] = new Array<BusinessFunctionFeatureDto>();
        if (features && Array.isArray(features)) {
            features.forEach((fe: any) => {
                // Handle form group values from FormArray
                const featureId = fe.featureId;
                const enabled = fe.enabled;

                if (featureId !== undefined && enabled !== undefined) {
                    switch (featureId) {
                        case Features.ContentIsUpToDate:
                            feat.push(new ContentIsUpToDateFeatureDto(enabled));
                            break;
                        case Features.CountryComparison:
                            feat.push(new CountryComparisonFeatureDto(enabled));
                            break;
                        case Features.AboutUs:
                            feat.push(new AboutUsFeatureDto(enabled));
                            break;
                        case Features.AuditHistory:
                            feat.push(new AuditHistoryFeatureDto(enabled));
                            break;
                        case Features.PdfDownload:
                            feat.push(new PdfDownloadFeatureDto(enabled));
                            break;
                        case Features.Timeline:
                            feat.push(new TimelineFeatureDto(enabled));
                            break;
                        case Features.PublishedDate:
                            feat.push(new PublishedDateFeatureDto(enabled));
                            break;
                        case Features.EmailNotification:
                            feat.push(new EmailNotificationFeatureDto(enabled));
                            break;
                        case Features.EmailDigest:
                            feat.push(new EmailDigestFeatureDto(enabled));
                            break;
                    }
                }
            });
        }
        return feat;
    }

    togglePathLocked(): void {
        this.pathLocked = !this.pathLocked;
    }


    ngAfterViewInit(): void {
        this.originalData = JSON.stringify(this.businessFunctionForm.value);
        // Initialize CKEditor link dialog fixes
        this.waitForCKEditorAndSetup();
    }

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener("window:beforeunload")
    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        let changedSet: string = JSON.stringify(this.businessFunctionForm.value);
        if (this.originalData !== changedSet) {
            if (this.confirmModal) {
                this.confirmModal.openModal();
            }
            return false;
        } else {
            return true;
        }
    }

    navigatingTo: string;

    onAccept(): void {
        // reset the the original back to whats currenlty in itemConfigSet, this allows to pass check  in candeactivate
        this.originalData = JSON.stringify(this.businessFunctionForm.value);
        this.router.navigate([this.navigatingTo]);
        this.confirmModal.closeModal();
    }

    onDecline(): void {
        this.confirmModal.closeModal();
    }

    showConfirmModal(): boolean {
        this.confirmModal.show();
        return false;
    }
    selectFile(imageInput) {
        this.imageExtension = imageInput.target.files[0].name.split(".").pop();
        this.imageExtInLowerCase = this.imageExtension.toLowerCase();
        var imageSize = imageInput.target.files[0].size;
        if (imageSize > 3145728) {
            this.imageErrorMsg = 'Image size cannot be more than 3 MB';
            this.encodedImage = "";
            return;
        }
        if (!imageInput.target.files[0] || imageInput.target.files[0].length == 0) {
            this.imageErrorMsg = 'You must select an image';
            this.encodedImage = "";
            return;
        }
        if (this.imageExtInLowerCase == "webp") {
            this.imageErrorMsg = 'Only JPG, PNG and JPEG image formats are allowed';
            this.encodedImage = "";
            this.removeImageButtonVisibility = false;
            return;
        }
        var mimeType = imageInput.target.files[0].type;
        if (mimeType.match(/image\/*/) == null) {
            this.imageErrorMsg = "Only images are supported";
            this.encodedImage = "";
            return;
        }
        if (this.imageExtInLowerCase == "jpg" || this.imageExtInLowerCase == "png" || this.imageExtInLowerCase == "jpeg") {
            var reader = new FileReader();
            reader.readAsDataURL(imageInput.target.files[0]);
            reader.onload = (_event: any) => {
                var img = new Image();
                img.src = reader.result as string;
                img.onload = () => {
                    const height = img.naturalHeight;
                    const width = img.naturalWidth;
                    if (height < 40 || width < 155) {
                        this.encodedImage = "";
                        this.imageErrorMsg = "Please upload an image with a minimum dimension of 155 X 45 ";
                        this.removeImageButtonVisibility = false;
                    }
                    else {
                        this.imageErrorMsg = "";
                        this.encodedImage = reader.result;
                        this.removeImageButtonVisibility = true;
                    }
                    this.cdr.detectChanges();
                };
            }
        }
        else {
            this.imageErrorMsg = 'Only JPG, PNG and JPEG image formats are allowed';
            this.encodedImage = "";
            this.removeImageButtonVisibility = false;
            return;
        }
    }
    deleteImage() {
        this.encodedImage = "";
        this.removeImageButtonVisibility = false;
        this.imageFile.nativeElement.value = '';
        this.cdr.detectChanges();
    }
    setLogoType(e: string): void {
        this.selectedLink = e;
        if (this.selectedLink == "Default") {
            this.encodedImage = "";
        }
        else {
            this.imageErrorMsg = "";
            this.encodedImage = this.logoImage;
            if (this.encodedImage != "") {
                this.removeImageButtonVisibility = true;
            }
            else {
                this.removeImageButtonVisibility = false;
            }
        }
        this.cdr.detectChanges();
    }
    isSelected(name: string): boolean {
        if (!this.selectedLink) {
            return false;
        }
        else {
            return (this.selectedLink === name);
        }
    }

    // CKEditor link dialog fix methods (similar to latestupdatemodal)
    private waitForCKEditorAndSetup(): void {
        let attempts = 0;
        const maxAttempts = 20; // Wait up to 10 seconds

        const checkForCKEditor = () => {
            attempts++;
            const ckElements = document.querySelectorAll('.ck-editor');
            const linkButton = document.querySelector('.ck-toolbar .ck-button[title*="Link"]');

            if (ckElements.length > 0 || attempts >= maxAttempts) {
                this.fixCKEditorZIndex();
                this.debugCKEditor();
                this.addCustomLinkHandler();
            } else {
                // Try again in 500ms
                setTimeout(checkForCKEditor, 500);
            }
        };

        checkForCKEditor();
    }

    private fixCKEditorZIndex(): void {
        // Apply high z-index to all CKEditor dialogs to ensure they appear properly
        const style = document.createElement('style');
        style.textContent = `
            .ck.ck-balloon-panel {
                z-index: 99999 !important;
            }
            .ck-link-form, .ck-link-actions {
                z-index: 99999 !important;
            }
        `;
        document.head.appendChild(style);
    }

    private debugCKEditor(): void {
        // More comprehensive search for link button
        const linkButtons = [
            document.querySelector('.ck-toolbar .ck-button[title*="Link"]'),
            document.querySelector('.ck-toolbar .ck-button[title*="link"]'),
            document.querySelector('.ck-toolbar .ck-button[data-cke-tooltip-text*="Link"]'),
            document.querySelector('.ck-toolbar .ck-button[aria-label*="Link"]')
        ];

        const linkButton = linkButtons.find(btn => btn !== null);

        if (linkButton) {
            // Add click event listener for proper link dialog positioning
            linkButton.addEventListener('click', (e) => {
                // Single timeout to position the dialog properly
                setTimeout(() => {
                    this.forceShowLinkDialog();
                }, 100);
            });
        }
    }

    private forceShowLinkDialog(): void {
        // Find the main link form/balloon panel AND link actions
        const linkForm = document.querySelector('.ck-link-form') as HTMLElement;
        const linkActions = document.querySelector('.ck-link-actions') as HTMLElement;
        const balloonPanel = document.querySelector('.ck-balloon-panel') as HTMLElement;

        // Hide any duplicate or extra balloons first
        const allBalloons = document.querySelectorAll('.ck-balloon-panel');
        allBalloons.forEach((balloon, index) => {
            const element = balloon as HTMLElement;
            if (index > 0) { // Hide all except the first one
                element.style.display = 'none';
            }
        });

        // Hide ONLY navigation buttons (Previous/Next), keep Save/Cancel buttons
        const navSelectors = [
            '.ck-balloon-rotator__navigation',
            '.ck-balloon-rotator__counter',
            '.ck-button[data-cke-tooltip-text="Previous"]',
            '.ck-button[data-cke-tooltip-text="Next"]',
            '.ck-button[title="Previous"]',
            '.ck-button[title="Next"]'
        ];

        navSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                const htmlElement = element as HTMLElement;
                htmlElement.style.display = 'none';
                htmlElement.style.visibility = 'hidden';
                htmlElement.style.opacity = '0';
            });
        });

        // Style both link form AND link actions (prefer linkForm, then linkActions, then balloonPanel)
        const primaryDialog = linkForm || linkActions || balloonPanel;

        // Also apply styling to link actions specifically if it exists
        if (linkActions) {
            linkActions.style.cssText += `
                z-index: 999999 !important;
                position: absolute !important;
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
                background: #fafafa !important;
                border: 1px solid #e0e0e0 !important;
                border-radius: 4px !important;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1) !important;
            `;
        }

        if (primaryDialog) {
            // Apply proper dialog styling with border and clean appearance
            primaryDialog.style.cssText += `
                z-index: 999999 !important;
                position: absolute !important;
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
                background: #fafafa !important;
                border: 1px solid #e0e0e0 !important;
                border-radius: 4px !important;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1) !important;
            `;
        }

        // Monitor and hide navigation buttons that might appear after dialog is shown
        setTimeout(() => this.hideNavigationButtons(), 200);
        setTimeout(() => this.hideNavigationButtons(), 500);

        // Also monitor for link actions popup that might appear when clicking existing links
        setTimeout(() => this.ensureLinkActionsVisible(), 200);
        setTimeout(() => this.ensureLinkActionsVisible(), 500);
    }

    private hideNavigationButtons(): void {
        // Only hide navigation buttons, preserve Save/Cancel/etc buttons
        const navSelectors = [
            '.ck-balloon-rotator__navigation',
            '.ck-balloon-rotator__counter',
            '.ck-button[data-cke-tooltip-text="Previous"]',
            '.ck-button[data-cke-tooltip-text="Next"]',
            '.ck-button[title="Previous"]',
            '.ck-button[title="Next"]'
        ];

        navSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                const htmlElement = element as HTMLElement;
                htmlElement.style.display = 'none';
                htmlElement.style.visibility = 'hidden';
                htmlElement.style.opacity = '0';
            });
        });
    }

    private ensureLinkActionsVisible(): void {
        // Find and ensure link actions popup is visible when clicking existing links
        const linkActions = document.querySelector('.ck-link-actions') as HTMLElement;
        const linkActionsBalloon = document.querySelector('.ck-balloon-panel.ck-link-actions') as HTMLElement;

        [linkActions, linkActionsBalloon].forEach(element => {
            if (element) {
                element.style.cssText += `
                    z-index: 999999 !important;
                    position: absolute !important;
                    display: block !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                    background: #fafafa !important;
                    border: 1px solid #e0e0e0 !important;
                    border-radius: 4px !important;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1) !important;
                    --ck-color-link-default: #00A3E0 !important;
                `;

                // Also fix any link elements inside the popup
                const linkElements = element.querySelectorAll('a, .ck-link-actions__preview');
                linkElements.forEach(link => {
                    (link as HTMLElement).style.color = '#00A3E0';
                });
            }
        });
    }

    private addCustomLinkHandler(): void {
        // Find all link buttons (multiple CKEditor instances) and add custom handlers
        const linkButtons = document.querySelectorAll('.ck-toolbar .ck-button[title*="Link"]') as NodeListOf<HTMLElement>;

        linkButtons.forEach((linkButton, index) => {
            // Remove existing listeners and add our custom one
            const newLinkButton = linkButton.cloneNode(true) as HTMLElement;
            linkButton.parentNode?.replaceChild(newLinkButton, linkButton);

            newLinkButton.addEventListener('click', (e) => {
                setTimeout(() => {
                    this.forceShowLinkDialog();
                }, 100);
            });
        });

        // Simple approach: Monitor for link actions that appear when clicking existing links
        this.monitorForLinkActions();
    }

    private monitorForLinkActions(): void {
        // Use MutationObserver to watch for link actions appearing
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as Element;
                            // Check if this is a link actions panel
                            if (element.classList?.contains('ck-link-actions') ||
                                element.querySelector?.('.ck-link-actions')) {
                                setTimeout(() => this.ensureLinkActionsVisible(), 50);
                            }
                        }
                    });
                }
            });
        });

        // Observe the entire document for link actions
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}

